/* eslint-disable consistent-return */
/* eslint-disable class-methods-use-this */
/* eslint-disable no-unused-expressions */
import axios, { CancelToken } from 'axios';
import urls from 'system/urls';
import currentUser from 'system/currentUser';

export default class Gateway {
  static cancel;

  static stop() {
    Gateway?.cancel?.();
  }

  getContentType(isMultipartForm) {
    return (!isMultipartForm ? 'application/json' : 'multipart/form-data');
  }

  getBodyData(isMultipartForm, body) {
    return (isMultipartForm ? body : JSON.stringify(body));
  }

  getResponseOrData(returnWholeResponse, response, data) {
    return (returnWholeResponse ? response : data);
  }

  getHasNoResponseOrCancelled(response, isCancelled) {
    return (!response || isCancelled);
  }

  getStatusDisabled(disableErrorResponseTypes, status) {
    return (disableErrorResponseTypes?.includes(status) || false);
  }

  getErrorStatusResponse(status) {
    switch (status) {
      case 500:
        return currentUser?.coreInterface?.serverError();
      case 503:
        return currentUser?.coreInterface?.unavailable();
      case 401:
        return currentUser?.coreInterface?.unauthorised();
      case 403:
        return currentUser?.coreInterface?.forbidden();
      // no default
    }
  }

  getStatusResonse(status, response, data, statusDisabled) {
    switch (status) {
      case 400:
        return Promise.reject(this.getResponseOrData(this.options.returnWholeResponse, response, data));
      case 404:
        if (!statusDisabled) currentUser?.coreInterface?.notFound();
        return Promise.reject(response);
      default:
        currentUser?.coreInterface?.onError(data);
        return Promise.reject(this.getResponseOrData(this.options.returnWholeResponse, response, data));
    }
  }

  constructor(url, options = { returnWholeResponse: false, disableErrorResponseTypes: [] }) {
    if (!url) {
      throw new Error('API initialized with no URL.');
    }

    // try to find url in predefined urls if it is missed, then use provided url
    this.url = urls[url]?.toString() || url;
    this.options = options;
  }

  /**
   * Generate the fetch request.
   * @param {string} url - The request URL.
   * @param {string} [type=GET] - The request type.
   * @param {string} [responseType=json] - The type of response to be returned.
   * @param {object} [body] - The request body.
   * @param {object} [headers] - The request headers.
   */
  generateFetch(url, type = 'GET', responseType = 'json', body, headers, isMultipartForm) {
    const config = {
      responseType,
      headers: {
        ...currentUser.get('httpHeaders'),
        Accept: 'application/json',
        'Content-Type': this.getContentType(isMultipartForm),
        'Access-Control-Allow-Origin': window.location.origin,
        'Access-Control-Allow-Credentials': 'true',
        'Access-Control-Allow-Methods': 'GET,HEAD,OPTIONS,POST,PUT,DELETE,PATCH',
        'Access-Control-Allow-Headers': 'Access-Control-Allow-Credentials, Access-Control-Allow-Origin, Origin, X-Requested-With, Content-Type, Accept, Authorization, Content-Disposition',
        ...headers
      },
      credentials: 'include',
      method: type,
      mode: 'cors',
      ...this.options,
      validateStatus: (status) => {
        switch (status) {
          case 200:
          case 201:
          case 202:
          case 204:
          case 409:
            return true;
          default:
            return false;
        }
      },
    };

    if (typeof body !== 'undefined') {
      config.data = this.getBodyData(isMultipartForm, body);
    }
    return axios.request({
      url,
      ...config,
      cancelToken: new CancelToken((cancel) => { Gateway.cancel = cancel; }),
    }).then((response) => {
      const { status } = response;

      if (status === 204) {
        return response;
      }

      const { data } = response;
      switch (status) {
        case 201:
        case 202:
          return Promise.resolve();
        case 200:
        case 204:
        case 409:
          return Promise.resolve(this.getResponseOrData(this.options.returnWholeResponse, response, data));
        case 400:
          return Promise.reject(this.getResponseOrData(this.options.returnWholeResponse, response, data));
        default:
          currentUser?.coreInterface?.onError(data);
          return Promise.reject(this.getResponseOrData(this.options.returnWholeResponse, response, data));
      }
    }).catch(({ response }) => {
      if (this.getHasNoResponseOrCancelled(response, axios.isCancel(response))) {
        return null;
      }
      const { status, data } = response;
      const statusDisabled = this.getStatusDisabled(this.options.disableErrorResponseTypes, status);

      if (!statusDisabled) {
        this.getErrorStatusResponse(status);
      }

      return this.getStatusResonse(status, response, data, statusDisabled);
    });
  }

  /**
   * Resolve dynamic parts within the URL.
   * @param {object} config - URL part/replacement pairs.
   * @returns {API} - this.
   */
  params(config) {
    if (!config || typeof config !== 'object') {
      return this;
    }

    let parameterisedUrl = this.url;

    Object.entries(config).forEach((entry) => {
      parameterisedUrl = parameterisedUrl.replace(`:${entry[0]}`, entry[1]);
    });

    this.url = parameterisedUrl;

    return this;
  }

  query(options) {
    if (!options) {
      return this;
    }
    let parameterisedUrl = this.url;
    let separator = '?';
    Object.keys(options).forEach((option) => {
      parameterisedUrl += `${separator}${option}=${encodeURIComponent(options[option])}`;
      separator = '&';
    });

    this.url = parameterisedUrl;

    return this;
  }

  /**
   * Execute a DELETE request.
   * @param {object} [headers] - The request headers.
   */
  delete(headers) {
    return this.generateFetch(this.url, 'DELETE', undefined, undefined, headers);
  }

  /**
   * Execute a GET request.
   * @param {string} [responseType=json] - The response type to be returned.
   * @param {object} [headers] - The request headers.
   */
  get(responseType, headers) {
    return this.generateFetch(this.url, 'GET', responseType, undefined, headers);
  }

  /**
   * Execute a POST request.
   * @param {object} body - The request body.
   * @param {string} [responseType=json] - The response type to be returned.
   * @param {object} [headers] - The request headers.
   */
  post(body, responseType, headers, isMultipartForm) {
    return this.generateFetch(this.url, 'POST', responseType, body, headers, isMultipartForm);
  }

  /**
   * Execute a PUT request.
   * @param {object} body - The request body.
   * @param {string} [responseType=json] - The response type to be returned.
   * @param {object} [headers] - The request headers.
   */
  put(body, responseType, headers, isMultipartForm) {
    return this.generateFetch(this.url, 'PUT', responseType, body, headers, isMultipartForm);
  }

  /**
   * Execute a PATCH request.
   * @param {object} body - The request body.
   * @param {string} [responseType=json] - The response type to be returned.
   * @param {object} [headers] - The request headers.
   */
  patch(body, responseType, headers, isMultipartForm) {
    return this.generateFetch(this.url, 'PATCH', responseType, body, headers, isMultipartForm);
  }
}
